import React from 'react';
import 'twin.macro';
import { Eleve } from 'types/eleves';
import { Checkboxes } from '@teetsh/app/src/components/Form/Checkboxes';
import Button from 'components/Buttons/Button';
import { CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import DeleteButton from 'components/Buttons/DeleteButton';
import { useTranslation } from 'react-i18next';
import { ColorSelector } from 'components/Form/ColorSelector';
import { Color } from 'types/colors';
import { EditingGroup } from './types';
import InputGroup from 'components/Form/InputGroup';

interface Props {
  eleves: Eleve[];
  onSubmit: () => void;
  onCancel: () => void;
  onDelete: () => void;
  value: EditingGroup;
  onChange: (g: EditingGroup) => void;
}

export function GroupCreator(props: Props) {
  const { onSubmit, onDelete, onChange, onCancel, value, eleves } = props;
  const { t } = useTranslation(['common', 'cahierJournal']);
  const [error, setError] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleSubmit = () => {
    if (!value.name?.trim()) {
      setError(true);
      inputRef.current?.focus();
      return;
    }
    onSubmit();
  };

  const handleNameChange = (e: string) => {
    setError(false);
    onChange({
      ...value,
      name: e,
    });
  };

  return (
    <div tw="pt-4">
      <div tw="space-y-4">
        <InputGroup
          inputRef={inputRef}
          label={t('cahierJournal:ateliers.elevesGroup.groupName')}
          id="groupName"
          type="text"
          value={value?.name || ''}
          error={
            error
              ? t('cahierJournal:ateliers.elevesGroup.errors.nameRequired')
              : undefined
          }
          onChange={handleNameChange}
          onKeyDown={e => {
            e.stopPropagation(); // prevent closing the disclosure
            if (e.key === 'Escape') {
              onCancel();
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
            if (e.key === ' ') {
              e.stopPropagation();
            }
          }}
          autoFocus
        />
        <ColorSelector
          value={value.color}
          onChange={v => {
            onChange({
              ...value,
              color: v as Color,
            });
          }}
        />
        {eleves.length > 0 ? (
          <div tw="space-y-2 text-sm">
            <div tw="font-medium">
              {t('cahierJournal:ateliers.elevesGroup.selectStudents')}
            </div>
            <Checkboxes
              options={eleves.map(eleve => ({
                label: `${eleve.givenName} ${eleve.familyName}`,
                value: eleve.id,
              }))}
              value={(value?.eleves || []).map(e => e.id)}
              onChange={v => {
                onChange({
                  ...value,
                  eleves: eleves.filter(e => v.includes(e.id)),
                });
              }}
              condensed
            />
          </div>
        ) : (
          <div tw="text-sm prose">
            {t('cahierJournal:ateliers.elevesGroup.noElevesMessage')}
            <br tw="my-1" />
            {t('cahierJournal:ateliers.elevesGroup.noElevesAction')}
          </div>
        )}
        <div tw="py-2 space-x-3 items-center flex justify-end sticky bottom-0 border-t -mx-4 px-4 bg-neutral-supershy">
          {value?.id && (
            <DeleteButton
              withConfirmation
              intent="danger"
              onClick={onDelete}
              minimal
              small
              iconSize="sm"
              icon={TrashIcon}
            />
          )}
          <Button onClick={onCancel} small>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            small
            icon={CheckIcon}
            intent="primary"
          >
            {value?.id ? 'Modifier' : 'Valider'}
          </Button>
        </div>
      </div>
    </div>
  );
}
